import datacentral from '@/utils/axios/datacentral'
import {
    OtherFinancialItem,
    CreateOtherFinancialItemRequest,
    UpdateOtherFinancialItemRequest,
    BatchCreateOtherFinancialItemsRequest,
    OtherFinancialItemListParams,
    OtherFinancialSummary,
    MonthlySummary,
    SummaryParams,
} from '../types'
import { ResponseArray, ResponseObject } from '@/types/shared'
import qs from 'query-string'

const BASE_URL = '/reports/bi/other-financial-items'

/**
 * Create a single financial item
 * @param data - Financial item data
 * @returns Promise with created financial item
 */
export const createOtherFinancialItem = async (
    data: CreateOtherFinancialItemRequest
): Promise<OtherFinancialItem> => {
    const response = await datacentral.post<ResponseObject<OtherFinancialItem>>(
        BASE_URL,
        data
    )
    return response.data.result
}

/**
 * Batch create multiple financial items (revenues and expenses)
 * @param data - Batch create request with revenues and expenses arrays
 * @returns Promise with created financial items
 */
export const batchCreateOtherFinancialItems = async (
    data: BatchCreateOtherFinancialItemsRequest
): Promise<OtherFinancialItem[]> => {
    const response = await datacentral.post<ResponseArray<OtherFinancialItem>>(
        `${BASE_URL}/batch`,
        data
    )
    return response.data?.results || []
}

/**
 * Get financial item by ID
 * @param id - Financial item ID
 * @returns Promise with financial item detail
 */
export const getOtherFinancialItemById = async (
    id: number
): Promise<OtherFinancialItem> => {
    const { data } = await datacentral.get<ResponseObject<OtherFinancialItem>>(
        `${BASE_URL}/${id}`
    )
    return data.result
}

/**
 * List financial items with optional filters
 * @param params - Filter parameters
 * @returns Promise with financial items list
 */
export const listOtherFinancialItems = async (
    params?: OtherFinancialItemListParams
): Promise<OtherFinancialItem[]> => {
    const queryString = qs.stringify(params || {}, { arrayFormat: 'bracket' })
    const { data } = await datacentral.get<ResponseArray<OtherFinancialItem>>(
        `${BASE_URL}?${queryString}`
    )
    return data?.results || []
}

/**
 * Update an existing financial item
 * @param id - Financial item ID
 * @param data - Updated financial item data
 * @returns Promise with updated financial item
 */
export const updateOtherFinancialItem = async (
    id: number,
    data: UpdateOtherFinancialItemRequest
): Promise<OtherFinancialItem> => {
    const response = await datacentral.put<ResponseObject<OtherFinancialItem>>(
        `${BASE_URL}/${id}`,
        data
    )
    return response.data.result
}

/**
 * Delete a financial item (soft delete)
 * @param id - Financial item ID
 * @returns Promise
 */
export const deleteOtherFinancialItem = async (id: number): Promise<void> => {
    await datacentral.delete(`${BASE_URL}/${id}`)
}

/**
 * Delete all financial items for a specific month
 * @param reportMonth - Month to delete (YYYY-MM)
 * @returns Promise
 */
export const deleteOtherFinancialItemsByMonth = async (
    reportMonth: string
): Promise<void> => {
    await datacentral.delete(`${BASE_URL}/month`, {
        params: { report_month: reportMonth },
    })
}

/**
 * Get financial summary with optional grouping by item type
 * @param params - Summary parameters
 * @returns Promise with financial summary
 */
export const getOtherFinancialSummary = async (
    params?: SummaryParams
): Promise<OtherFinancialSummary[]> => {
    const queryString = qs.stringify(params || {}, { arrayFormat: 'bracket' })
    const { data } = await datacentral.get<
        ResponseArray<OtherFinancialSummary>
    >(`${BASE_URL}/summary?${queryString}`)
    return data?.results || []
}

/**
 * Get monthly summary showing revenue, expense, and net amount
 * @param params - Summary parameters
 * @returns Promise with monthly summary
 */
export const getMonthlySummary = async (
    params?: Omit<SummaryParams, 'group_by_item_type'>
): Promise<MonthlySummary[]> => {
    const queryString = qs.stringify(params || {}, { arrayFormat: 'bracket' })
    const { data } = await datacentral.get<ResponseArray<MonthlySummary>>(
        `${BASE_URL}/monthly-summary?${queryString}`
    )
    return data?.results || []
}
